import type { EntityListResponse } from '@ydistri/api-sdk';
import { ScopeSectionRuleTypeName } from '@ydistri/api-sdk';
import type { ScopeDraft } from './scopeEditorModel';
import { getDraftValidationIssues } from './scopeEditorModel';

type ScopeWarningEntityList = Pick<EntityListResponse, 'entityListId' | 'isArchived' | 'name'>;

export interface ScopeEditorWarnings {
  ruleMessagesById: Record<string, string[]>;
  sectionIdsWithWarnings: string[];
  summaryMessages: string[];
}

const addRuleMessage = (
  ruleMessagesById: Record<string, string[]>,
  ruleId: string,
  message: string,
): void => {
  ruleMessagesById[ruleId] = [...(ruleMessagesById[ruleId] ?? []), message];
};

export const getScopeEditorWarnings = (
  draft: ScopeDraft,
  entityLists: ScopeWarningEntityList[] = [],
): ScopeEditorWarnings => {
  const ruleMessagesById: Record<string, string[]> = {};
  const sectionIdsWithWarnings = new Set<string>();
  const summaryMessages: string[] = [];

  getDraftValidationIssues(draft).forEach(issue => {
    const message = `Unsaved: ${issue.message}`;
    if (issue.ruleId) {
      addRuleMessage(ruleMessagesById, issue.ruleId, message);
    } else if (issue.sectionId) {
      sectionIdsWithWarnings.add(issue.sectionId);
    } else {
      summaryMessages.push(message);
    }
  });

  const entityListsById = new Map(
    entityLists.map(entityList => [entityList.entityListId, entityList]),
  );

  draft.sections.forEach(section => {
    section.rules
      .filter(rule => rule.type === ScopeSectionRuleTypeName.EntityList)
      .forEach(rule => {
        rule.valueIds.forEach(entityListId => {
          const entityList = entityListsById.get(entityListId);
          if (!entityList?.isArchived) return;

          const name = entityList.name?.trim() ?? `Entity list ${entityListId}`;
          addRuleMessage(
            ruleMessagesById,
            rule.id,
            `Selected custom list "${name}" is archived. It continues to resolve until removed.`,
          );
        });
      });
  });

  return { ruleMessagesById, sectionIdsWithWarnings: [...sectionIdsWithWarnings], summaryMessages };
};
